import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import axios from '../utils/axiosInstance';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import useTargetLocales from '../hooks/useTargetLocales';
import { localeLabel } from '../utils/localeFlag';

// Phase 22 — per-locale translation status badge, same style + vocab as
// ThemeStrings so users see a single consistent "is this pushed?" signal
// across every translatable surface.
function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  const map = {
    pending:    { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100',         label: 'Pending' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',         label: 'Translated' },
    pushed:     { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed:     { className: 'bg-red-100 text-red-800 hover:bg-red-100',             label: 'Failed' },
    skipped:    { className: 'bg-zinc-100 text-zinc-600 hover:bg-zinc-100',          label: 'Skipped' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

export default function MetafieldDetail() {
  const { ownerType, id } = useParams();
  const navigate = useNavigate();
  const { locales } = useTargetLocales();

  const [definition, setDefinition] = useState(null);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('count'); // 'count' | 'alpha'
  const [expandedValue, setExpandedValue] = useState(null);
  const [productsCache, setProductsCache] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState(null);

  const setTranslationPolicy = async (newPolicy) => {
    if (definition?.translation_policy === newPolicy) return;
    setPolicySaving(true);
    setPolicyError(null);
    try {
      const res = await axios.patch(
        `/api/transly/metafields/definitions/${id}/`,
        { translation_policy: newPolicy }
      );
      setDefinition(res.data.definition);
    } catch (err) {
      setPolicyError(
        err.response?.data?.error || err.message || 'Failed to update policy'
      );
    } finally {
      setPolicySaving(false);
    }
  };

  // Race condition guard ref
  const expandingValueRef = useRef(null);

  const isProduct = ownerType === 'product';
  const backLabel = isProduct ? 'Product Metafields' : 'Variant Metafields';
  const columnLabel = isProduct ? 'Products' : 'Variants';

  // Fetch definition and values
  useEffect(() => {
    let cancelled = false;
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/api/transly/metafields/definitions/${id}/`);
        if (!cancelled) {
          setDefinition(res.data.definition);
          setValues(res.data.values || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error || err.message || 'Failed to load values'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDetail();
    return () => { cancelled = true; };
  }, [id]);

  // Expand/collapse with caching and race-condition guard
  const handleToggleExpand = useCallback(
    async (rawValue) => {
      if (expandedValue === rawValue) {
        // Collapse
        setExpandedValue(null);
        expandingValueRef.current = null;
        return;
      }

      // Expand
      setExpandedValue(rawValue);

      // Cache hit -- no fetch needed
      if (productsCache[rawValue]) return;

      // Track which value we are fetching for (race guard)
      expandingValueRef.current = rawValue;
      setLoadingProducts(true);

      try {
        const res = await axios.get(
          `/api/transly/metafields/definitions/${id}/products/`,
          { params: { value: rawValue } }
        );

        // Discard stale response if user clicked a different row while in flight
        if (expandingValueRef.current !== rawValue) return;

        setProductsCache((prev) => ({
          ...prev,
          [rawValue]: res.data.products || [],
        }));
      } catch {
        // Discard stale
        if (expandingValueRef.current !== rawValue) return;

        setProductsCache((prev) => ({
          ...prev,
          [rawValue]: [],
        }));
      } finally {
        if (expandingValueRef.current === rawValue) {
          setLoadingProducts(false);
        }
      }
    },
    [expandedValue, productsCache, id]
  );

  // Sort and filter values
  const processedValues = useMemo(() => {
    let result = values;

    // Filter by search
    if (search) {
      const term = search.toLowerCase();
      result = result.filter((v) =>
        (v.raw_value || '').toLowerCase().includes(term)
      );
    }

    // Sort
    if (sortBy === 'count') {
      result = [...result].sort((a, b) => b.usage_count - a.usage_count);
    } else {
      result = [...result].sort((a, b) =>
        (a.raw_value || '').localeCompare(b.raw_value || '')
      );
    }

    return result;
  }, [values, search, sortBy]);

  const noResultsForSearch =
    processedValues.length === 0 && values.length > 0 && search.length > 0;

  const handleReload = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`/api/transly/metafields/definitions/${id}/`)
      .then((res) => {
        setDefinition(res.data.definition);
        setValues(res.data.values || []);
      })
      .catch((err) => {
        setError(
          err.response?.data?.error || err.message || 'Failed to load values'
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate(`/metafields/${ownerType}`)}
        className="text-sm text-muted-foreground hover:text-foreground cursor-pointer flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" />
        {backLabel}
      </button>

      {/* Header */}
      {definition && (
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {definition.name || definition.key}
            </h1>
            <p className="text-sm text-muted-foreground">
              {definition.namespace}.{definition.key} | {definition.type_name} |{' '}
              {values.length} values
            </p>
          </div>
          <Input
            type="text"
            placeholder="Search values..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
        </div>
      )}

      {/* Translation policy panel */}
      {definition && (
        <div className="border border-border rounded-md p-4 mb-6 bg-muted/30">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-medium text-foreground mb-1">
                Translation policy
              </div>
              <p className="text-xs text-muted-foreground">
                Controls whether values for this definition get translated to
                DE/ES and pushed to Shopify. Defaults to skip when unset.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={definition.translation_policy === 'translate' ? 'default' : 'outline'}
                size="sm"
                disabled={policySaving}
                onClick={() => setTranslationPolicy('translate')}
              >
                Translate
              </Button>
              <Button
                variant={definition.translation_policy === 'skip' ? 'default' : 'outline'}
                size="sm"
                disabled={policySaving}
                onClick={() => setTranslationPolicy('skip')}
              >
                Skip
              </Button>
              <Button
                variant={!definition.translation_policy ? 'default' : 'outline'}
                size="sm"
                disabled={policySaving}
                onClick={() => setTranslationPolicy(null)}
              >
                Unset
              </Button>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
            <span>Translated rows (DE+ES): <strong>{definition.translated_count ?? 0}</strong></span>
            <span>·</span>
            <span>Pushed to Shopify: <strong>{definition.pushed_count ?? 0}</strong></span>
            {policySaving && <span className="text-foreground">Saving…</span>}
            {policyError && <span className="text-destructive">{policyError}</span>}
          </div>
        </div>
      )}

      {/* Sort controls */}
      {!loading && !error && values.length > 0 && (
        <div className="flex gap-1 mb-4">
          <Button
            variant={sortBy === 'count' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setSortBy('count')}
          >
            Most used
          </Button>
          <Button
            variant={sortBy === 'alpha' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setSortBy('alpha')}
          >
            A-Z
          </Button>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="flex flex-col items-center py-12">
          <p className="text-sm text-destructive mb-4">Failed to load values.</p>
          <Button variant="outline" onClick={handleReload}>
            Reload Values
          </Button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex gap-4 px-2 py-3">
              <div className="animate-pulse bg-muted h-4 rounded w-48" />
              <div className="animate-pulse bg-muted h-4 rounded w-16" />
            </div>
          ))}
        </div>
      )}

      {/* Empty values state */}
      {!loading && !error && values.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No values found for this metafield definition.
        </p>
      )}

      {/* No results for search */}
      {!loading && !error && noResultsForSearch && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No values match your search.
        </p>
      )}

      {/* Values table — Phase 22: primary signal is per-locale push status,
          usage count + product drill-in kept as secondary. */}
      {!loading && !error && processedValues.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Value</TableHead>
              <TableHead className="w-24 text-right text-muted-foreground">
                {columnLabel}
              </TableHead>
              {locales.map((loc) => (
                <TableHead key={loc} className="w-28">{localeLabel(loc)}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {processedValues.map((v) => {
              const isExpanded = expandedValue === v.raw_value;
              const cachedProducts = productsCache[v.raw_value];
              const colSpan = 2 + locales.length;

              return (
                <React.Fragment key={v.raw_value}>
                  {/* Value row */}
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => handleToggleExpand(v.raw_value)}
                  >
                    <TableCell className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                      )}
                      {v.raw_value}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground text-sm">
                      {v.usage_count}
                    </TableCell>
                    {locales.map((loc) => (
                      <TableCell key={loc}>
                        <StatusBadge status={v[`${loc}_status`]} />
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Expanded product rows */}
                  {isExpanded && (
                    <>
                      {loadingProducts && !cachedProducts && (
                        <TableRow>
                          <TableCell colSpan={colSpan} className="pl-8">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <div className="h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                              Loading...
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {cachedProducts && cachedProducts.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={colSpan}
                            className="pl-8 text-sm text-muted-foreground"
                          >
                            No products found with this value.
                          </TableCell>
                        </TableRow>
                      )}
                      {cachedProducts &&
                        cachedProducts.map((product, idx) => {
                          const productId = product.owner_product_gid?.replace(
                            'gid://shopify/Product/',
                            ''
                          );
                          return (
                            <TableRow
                              key={product.owner_gid || idx}
                              className="cursor-pointer hover:bg-accent/50"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (productId) {
                                  navigate(`/products/${productId}`);
                                }
                              }}
                            >
                              <TableCell
                                colSpan={colSpan}
                                className="pl-8 text-sm text-muted-foreground hover:text-foreground"
                              >
                                {product.owner_title}
                                {product.owner_sku && (
                                  <span className="text-muted-foreground ml-2">
                                    {product.owner_sku}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
